import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Signature, Camera, FileText, Package, MagnifyingGlass } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";

export default function Documents() {
  const nav = useNavigate();
  const [tab, setTab] = useState("signatures");
  const [q, setQ] = useState("");
  const [signatures, setSignatures] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [orders, setOrders] = useState([]);
  const [chMap, setChMap] = useState({});

  useEffect(() => {
    (async () => {
      const [s, p, o, ch] = await Promise.all([
        api.get("/signatures"),
        api.get("/photos"),
        api.get("/material-orders"),
        api.get("/chantiers"),
      ]);
      setSignatures(s.data);
      setPhotos(p.data);
      setOrders(o.data);
      setChMap(Object.fromEntries(ch.data.map((c) => [c.id, c])));
    })();
  }, []);

  const tabs = [
    { k: "signatures", label: "Signatures", icon: Signature, count: signatures.length },
    { k: "photos", label: "Photos", icon: Camera, count: photos.length },
    { k: "orders", label: "Commandes", icon: Package, count: orders.length },
  ];

  const filter = (list, keys) => q ? list.filter((x) => keys.some((k) => (x[k] || "").toLowerCase().includes(q.toLowerCase()))) : list;

  return (
    <div className="p-4 space-y-4">
      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="h-12 pl-11 rounded-2xl bg-white/5 border-white/10" data-testid="documents-search" />
      </div>

      <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1">
        {tabs.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`flex-1 flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-full ${tab === t.k ? "bg-[#005CFF] text-white" : "text-white/60"}`}
            data-testid={`doc-tab-${t.k}`}
          >
            <t.icon size={14} weight="bold" /> {t.label} <span className="text-[10px] opacity-60">({t.count})</span>
          </button>
        ))}
      </div>

      {tab === "signatures" && (
        <div className="space-y-2">
          {filter(signatures, ["client_name"]).length === 0 ? <Empty text="Aucune signature" icon={Signature} /> :
            filter(signatures, ["client_name"]).map((s) => (
              <button key={s.id} onClick={() => chMap[s.chantier_id] && nav(`/chantiers/${s.chantier_id}`)}
                className="w-full text-left rounded-2xl bg-white/[0.03] border border-white/10 p-3 flex items-center gap-3 hover:border-white/20 active:scale-[0.98] transition-transform"
                data-testid={`doc-sig-${s.id}`}>
                <img src={fileUrl(s.id)} className="w-20 h-14 rounded-lg bg-white object-contain" alt="" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{s.client_name}</div>
                  <div className="text-xs text-white/50 truncate">{chMap[s.chantier_id]?.name || "—"}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5">{new Date(s.created_at).toLocaleString("fr-FR")}</div>
                </div>
              </button>
            ))}
        </div>
      )}

      {tab === "photos" && (
        <div className="grid grid-cols-3 gap-2">
          {photos.length === 0 ? <div className="col-span-3"><Empty text="Aucune photo" icon={Camera} /></div> :
            photos.map((p) => (
              <button key={p.id} onClick={() => chMap[p.chantier_id] && nav(`/chantiers/${p.chantier_id}`)} className="aspect-square rounded-xl overflow-hidden bg-white/5 border border-white/10 relative active:scale-95 transition-transform" data-testid={`doc-photo-${p.id}`}>
                <img src={fileUrl(p.id)} alt="" className="w-full h-full object-cover" />
                <span className="absolute bottom-1 left-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-black/60">{p.kind}</span>
              </button>
            ))}
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-2">
          {orders.length === 0 ? <Empty text="Aucune commande" icon={Package} /> :
            orders.map((o) => (
              <button key={o.id} onClick={() => chMap[o.chantier_id] && nav(`/chantiers/${o.chantier_id}`)}
                className="w-full text-left rounded-2xl bg-white/[0.03] border border-white/10 p-3 hover:border-white/20 active:scale-[0.98] transition-transform"
                data-testid={`doc-order-${o.id}`}>
                <div className="flex justify-between items-start">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">{chMap[o.chantier_id]?.name || "—"}</div>
                    <div className="font-medium mt-0.5">{o.supplier || "Fournisseur ?"} · {o.items.length} articles</div>
                    <div className="text-[10px] text-white/40">{new Date(o.created_at).toLocaleString("fr-FR")}</div>
                  </div>
                  {o.total != null && <div className="font-display font-bold">{o.total.toFixed(2)} €</div>}
                </div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function Empty({ text, icon: Icon }) {
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-dashed border-white/10 p-8 text-center">
      <Icon size={28} className="mx-auto text-white/40 mb-2" weight="duotone" />
      <div className="text-sm text-white/50">{text}</div>
    </div>
  );
}
